import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import ControlHorariLayout from "./ControlHorariLayout";
import { chFetch, ChSessioCaducadaError } from "./chAuth";
import { CH_ETIQUETA_EVENT, CH_ICONA_EVENT, CH_CLASSE_EVENT } from "./chEtiquetes";
import { chFormatHora } from "./chDataHora";
import "./controlhorari.css";

const NOMS_MES = [
  "Gener", "Febrer", "Març", "Abril", "Maig", "Juny",
  "Juliol", "Agost", "Setembre", "Octubre", "Novembre", "Desembre",
];

function capitalitzar(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatCapDia(dataISO) {
  const data = new Date(`${dataISO}T00:00:00`);
  try {
    return capitalitzar(
      new Intl.DateTimeFormat("ca-ES", { weekday: "long", day: "numeric", month: "long" }).format(data)
    );
  } catch {
    return dataISO;
  }
}

// Consulta de l'històric de jornades al mòbil (CURRENT_TASK.md §33):
// navegació per mes i, per cada dia, la línia temporal real d'events.
function ControlHorariHistoric() {
  const ara = new Date();
  const [any, setAny] = useState(ara.getFullYear());
  const [mes, setMes] = useState(ara.getMonth() + 1); // 1-12
  const [dies, setDies] = useState([]);
  const [carregant, setCarregant] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const carregar = useCallback(async () => {
    setCarregant(true);
    try {
      const resposta = await chFetch(`/ControlHorariHistoric.php?any=${any}&mes=${mes}`);
      const dades = await resposta.json();
      if (!dades.success) {
        setError(dades.message || "No s'ha pogut carregar l'històric.");
        setDies([]);
        return;
      }
      setDies(dades.dies || []);
      setError("");
    } catch (err) {
      if (err instanceof ChSessioCaducadaError) {
        navigate("/Control_Horari/login", { replace: true });
        return;
      }
      setError("No s'ha pogut connectar amb el servidor.");
    } finally {
      setCarregant(false);
    }
  }, [any, mes, navigate]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const anarMesAnterior = () => {
    if (mes === 1) {
      setMes(12);
      setAny((a) => a - 1);
    } else {
      setMes((m) => m - 1);
    }
  };

  const anarMesSeguent = () => {
    if (mes === 12) {
      setMes(1);
      setAny((a) => a + 1);
    } else {
      setMes((m) => m + 1);
    }
  };

  return (
    <ControlHorariLayout titol="Històric" ajuda="historic">
      <div className="ch-historic">
        <div className="ch-historic-navegacio">
          <button type="button" className="ch-boto-icona" onClick={anarMesAnterior} aria-label="Mes anterior">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="ch-historic-mes">
            {NOMS_MES[mes - 1]} {any}
          </span>
          <button type="button" className="ch-boto-icona" onClick={anarMesSeguent} aria-label="Mes següent">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {error && (
          <div className="ch-avis-error">
            <FontAwesomeIcon icon={faCircleExclamation} /> {error}
          </div>
        )}

        {carregant ? (
          <div className="ch-carregant">Carregant...</div>
        ) : dies.length === 0 ? (
          <p className="ch-text-suau">No hi ha cap jornada registrada aquest mes.</p>
        ) : (
          dies.map(({ fullDiari, events }) => (
            <div className="ch-targeta" key={fullDiari.idFullDiari}>
              <div className="ch-targeta-capcalera">
                <span>{formatCapDia(fullDiari.Data)}</span>
                <span className={`ch-etiqueta-estat ${fullDiari.HoraSortida ? "ch-etiqueta-tancada" : "ch-etiqueta-oberta"}`}>
                  {fullDiari.HoraSortida ? "Tancada" : "En jornada"}
                </span>
              </div>
              {events.map((ev) => (
                <div className="ch-fila-event" key={ev.idRegistreJornadaEvent}>
                  <span className={`ch-icona-cercle ch-icona-petita ${CH_CLASSE_EVENT[ev.TipusEvent]}`}>
                    <FontAwesomeIcon icon={CH_ICONA_EVENT[ev.TipusEvent]} />
                  </span>
                  <div className="ch-fila-event-text">
                    <span>
                      {CH_ETIQUETA_EVENT[ev.TipusEvent]}
                      {ev.TipusPausaNom ? ` · ${ev.TipusPausaNom}` : ""}
                    </span>
                  </div>
                  <span className="ch-hora-dreta">{chFormatHora(ev.DataHoraEvent)}</span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </ControlHorariLayout>
  );
}

export default ControlHorariHistoric;
